import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { OutboundLabelForm } from "../outbound-labels";
import { InitialFeeModal } from "./initial-fee-modal";

export const OutboundLabelsGeneral = memo(function OutboundLabelsGeneral({
  input,
}: {
  input: OutboundLabelForm;
}) {
  const {
    newOmsEnabled,
    showRedoLogoOnLabel,
    automationDelaySeconds,
    binNamespace,
    binKey,
    fees,
  } = input.inputs;
  const subscriptionFee = fees.inputs.monthlySubscriptionFee.inputs;

  const params = useParams();
  const teamId = params.teamId!;
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [initialFeeModalOpen, setInitialFeeModalOpen] = useState(false);

  const handleOmsToggle = useHandler((value: boolean) => {
    if (value && !subscriptionFee.amount.value) {
      // Enabling OMS but no fee set, prompt for initial fee
      setInitialFeeModalOpen(true);
      return;
    }
    newOmsEnabled.setValue(value);
  });

  const handleInitialFeeSubmit = useHandler((fee: number, turnedOnAt: Date) => {
    subscriptionFee.amount.setValue(fee);
    subscriptionFee.turnedOnAt.setValue(turnedOnAt);
    newOmsEnabled.setValue(true);
    // When we enable OMS we should also enable the credit card processing fee
    input.inputs.chargeCreditCardProcessingFee.setValue(true);
    setInitialFeeModalOpen(false);
  });

  const [refreshProgress, setRefreshProgress] = useState<{
    status: "running" | "done" | "idle";
    total: number;
    started: number;
  } | null>(null);
  const [isWatching, setIsWatching] = useState(false);

  useEffect(() => {
    if (!isWatching) {
      return;
    }
    let cancelled = false;
    const abort = new AbortController();
    (async () => {
      // Reconnect loop: long-lived HTTP streams can be closed by edge proxies
      // or load balancers without any error or terminal status, which would
      // otherwise leave the UI frozen at the last-seen progress value. Keep
      // reopening the stream until either the workflow reports a terminal
      // status, the user navigates away, or we hit a non-abort error.
      while (!cancelled) {
        let sawTerminal = false;
        try {
          const iter = rpcClient.watchRefreshTeamRatesProgress(
            { teamId },
            { signal: abort.signal },
          );
          for await (const update of iter) {
            setRefreshProgress(update);
            if (update.status !== "running") {
              sawTerminal = true;
              setIsWatching(false);
              break;
            }
          }
        } catch (err) {
          // Intentional cleanup abort — exit the loop silently.
          if (abort.signal.aborted || cancelled) {
            return;
          }
          console.error("Error watching refresh progress, reconnecting", err);
        }
        if (sawTerminal || cancelled || abort.signal.aborted) {
          return;
        }
        // Stream ended without a terminal status (proxy timeout, connection
        // reset, etc). Pause briefly then reconnect.
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    })();
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [isWatching, rpcClient, teamId]);

  const [refreshRatesLoad, refreshRates] = useTriggerLoad(async () => {
    try {
      await rpcClient.refreshTeamRates({ teamId });
      setIsWatching(true);
    } catch (error) {
      console.error("Failed to refresh team rates:", error);
      alert("Failed to refresh team rates. Please try again later.");
    }
  });
  const handleRefreshRates = useHandler(async () => refreshRates());

  // On mount (and when teamId changes), peek once at the workflow state. If a
  // refresh is already running, start streaming progress so the admin can see
  // it even without clicking the button.
  useEffect(() => {
    let cancelled = false;
    const abort = new AbortController();
    (async () => {
      try {
        const iter = rpcClient.watchRefreshTeamRatesProgress(
          { teamId },
          { signal: abort.signal },
        );
        const first = await iter.next();
        if (cancelled || first.done) {
          return;
        }
        setRefreshProgress(first.value);
        if (first.value.status === "running") {
          setIsWatching(true);
        }
      } catch {
        // No workflow running; ignore.
      } finally {
        abort.abort();
      }
    })();
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [rpcClient, teamId]);

  const [resyncGroupsLoad, resyncGroups] = useTriggerLoad(async () => {
    try {
      await rpcClient.resyncFulfillmentGroups({ teamId });
      alert(
        "Fulfillment groups resync initiated. This may take several minutes to complete.",
      );
    } catch (error) {
      console.error("Failed to resync fulfillment groups:", error);
      alert("Failed to resync fulfillment groups. Please try again later.");
    }
  });
  const handleResyncGroups = useHandler(async () => resyncGroups());

  const groupsNeedingResyncCountLoader = useLoad(async () => {
    return await rpcClient.getGroupsNeedingResyncCount({ teamId });
  }, [teamId, resyncGroupsLoad.pending]);

  const [releaseProblemOrdersLoad, releaseProblemOrders] = useTriggerLoad(
    async () => {
      try {
        const result = await rpcClient.releaseProblemOrdersWhereNonePicked({
          teamId,
        });
        alert(
          `Released ${result.releasedCount} problem order${result.releasedCount === 1 ? "" : "s"} back to the pick queue.`,
        );
      } catch (error) {
        console.error("Failed to release problem orders:", error);
        alert("Failed to release problem orders. Please try again later.");
      }
    },
  );
  const handleReleaseProblemOrders = useHandler(async () =>
    releaseProblemOrders(),
  );

  return (
    <Card title="General">
      <Flex dir="column" gap="lg">
        <RedoToggle
          label="Enable OMS"
          setValue={handleOmsToggle}
          value={newOmsEnabled.value}
        />
        <RedoIncrementDecrement
          description="Seconds to wait before running automations on new fulfillment groups. We will not get rates until after automations have completed."
          label="Automation Delay (seconds)"
          min={0}
          setValue={(value) =>
            automationDelaySeconds.setValue(value.toString())
          }
          step={1}
          value={Number(automationDelaySeconds.value)}
        />
        <RedoTextInput
          label="Bin Namespace"
          placeholder="custom"
          setValue={binNamespace.setValue}
          value={binNamespace.value}
        />
        <RedoTextInput
          label="Bin Key"
          placeholder="bin"
          setValue={binKey.setValue}
          value={binKey.value}
        />
        <RedoToggle
          label="Show Redo logo on label (USPS only)"
          setValue={showRedoLogoOnLabel.setValue}
          value={showRedoLogoOnLabel.value}
        />

        <Flex dir="column" gap="md" mt="xl">
          <RedoButton
            hierarchy="primary"
            onClick={handleRefreshRates}
            pending={refreshRatesLoad.pending}
            text={
              refreshRatesLoad.pending
                ? "Refreshing rates..."
                : "Refresh Team Rates"
            }
            theme="normal"
          />
          <Text fontSize="xs" textAlign="left" textColor="secondary">
            Refreshes shipping rates for all of this team's shipments that need
            rates. Labels will be unable to be purchased for shipments with
            pending rates, so be careful and warn the merchant about the
            interruption.
          </Text>
          {refreshProgress?.status === "running" && (
            <Flex dir="column" gap="sm">
              <RedoProgressBar
                label
                labelOrientation="right"
                labelStyle="text"
                percentage={
                  refreshProgress.total > 0
                    ? Math.round(
                        (refreshProgress.started / refreshProgress.total) * 100,
                      )
                    : 0
                }
              />
              <Text fontSize="xs" textAlign="left" textColor="secondary">
                {`Started ${refreshProgress.started} of ${refreshProgress.total} shipment refreshes`}
              </Text>
            </Flex>
          )}
        </Flex>
        <Flex dir="column" gap="md" mt="xl">
          <RedoButton
            disabled={resyncGroupsLoad.pending}
            hierarchy="primary"
            onClick={handleResyncGroups}
            pending={resyncGroupsLoad.pending}
            text={
              resyncGroupsLoad.pending
                ? "Resyncing open fulfillments..."
                : "Resync open fulfillments"
            }
            theme="normal"
          />
          <Text fontSize="xs" textColor="secondary">
            {`Forces resync of all open shipments in this team${
              groupsNeedingResyncCountLoader.value !== undefined &&
              groupsNeedingResyncCountLoader.value > 0
                ? ` (currently ${groupsNeedingResyncCountLoader.value} shipment${
                    groupsNeedingResyncCountLoader.value === 1 ? "" : "s"
                  } being resynced)`
                : ""
            }`}
          </Text>
        </Flex>
        <Flex dir="column" gap="md" mt="xl">
          <RedoButton
            disabled={releaseProblemOrdersLoad.pending}
            hierarchy="primary"
            onClick={handleReleaseProblemOrders}
            pending={releaseProblemOrdersLoad.pending}
            text={
              releaseProblemOrdersLoad.pending
                ? "Releasing problem orders..."
                : "Release all problem orders where none are picked"
            }
            theme="normal"
          />
          <Text fontSize="xs" textColor="secondary">
            Finds all PROBLEM pick orders where no items were picked (verified
            against the pick session) and releases them back to the pick queue.
          </Text>
        </Flex>
      </Flex>
      <InitialFeeModal
        onClose={() => setInitialFeeModalOpen(false)}
        onSubmit={handleInitialFeeSubmit}
        open={initialFeeModalOpen}
      />
    </Card>
  );
});
